import { useQuery, type UseQueryOptions } from "react-query";

import {
  getDataObjectDetails,
  getDataObjectsForWorkflow,
  getDataObjectTypes,
  getDataObjectWorkflowRelationActions,
  getDataObjectActivityLogs
} from "../services/dataObjects.services";

import type {
  CreateDataObjectDetailsModelReturn,
  DataObjectWorkflowRelation,
  GetDataObjectDetailsRequestParams
} from "../types/dataObjects.types";
import type { UseQueryReturnType } from "@certa/common/types";

/**
 * @deprecated - use useGetDataObjectsDynamicAPI instead
 */
export const useGetDataObjectsForWorkflow = (
  workflowId: number,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectsForWorkflow>
  > = {
    enabled: true
  }
) => {
  return useQuery({
    enabled: !!workflowId && !!config?.enabled,
    retry: 0,
    queryKey: ["dataObjects", workflowId],
    queryFn: () => getDataObjectsForWorkflow(workflowId)
  });
};

export const useGetDataObjectTypes = (config?: { enabled?: boolean }) => {
  return useQuery({
    enabled: !!config?.enabled,
    queryKey: ["dataObjectTypes"],
    queryFn: () => getDataObjectTypes()
  });
};

type UseGetDataObjectsDynamicAPIParams = {
  filters: {
    objectId?: string;
    objectTypeTag?: string[];
    outgoingRelationshipsToObjectIds?: string[];
    outgoingRelationshipsForwardRelationName?: string[];
    workflowMappingWorkflowId?: string[];
    relatedVia?: {
      relationName: string;
      relatedObjectUid: string;
      objectTypeTag: string;
    };
    statusTagIn?: string[];
    statusTagNotIn?: string[];
    nameIContains?: string;
    orderBy?: string;
  };
};

export const useGetDataObjectsDynamicAPI = (
  params: UseGetDataObjectsDynamicAPIParams,
  offset: number = 0,
  config: UseQueryOptions<UseQueryReturnType<typeof getDataObjectDetails>> = {
    enabled: true
  }
) => {
  const { filters } = params;

  const objectTypeTag = filters.objectTypeTag?.join(",");
  const outgoingRelationshipsToObjectIds =
    filters.outgoingRelationshipsToObjectIds?.join(",");
  const outgoingRelationshipsForwardRelationName =
    filters.outgoingRelationshipsForwardRelationName?.join(",");
  const workflowMappingWorkflowId =
    filters.workflowMappingWorkflowId?.join(",");
  const relatedVia = filters.relatedVia;
  const objectId = filters.objectId;
  // We need to sort created_at by descending order by default since the
  // recently created objects should be at the top of the list
  const orderBy = filters?.orderBy || "-created_at";
  const nameIContains = filters?.nameIContains;
  const statusTagIn = filters?.statusTagIn?.join(",");
  const statusTagNotIn = filters?.statusTagNotIn?.join(",");

  const transformedParams: GetDataObjectDetailsRequestParams = {
    data: {
      id: null, // TODO: Remove this once create workflow supports uid for object
      uid: null,
      name: null,
      data: null,
      logo: null,
      applied_badges: null,
      created_at: null,
      updated_at: null,
      object_type: {
        id: null,
        tag: null,
        schema: null,
        created_at: null,
        updated_at: null,
        ui_schema: null
      },
      status: {
        id: null,
        tag: null,
        name: null,
        color_code: null
      },
      workflow_mappings: {
        id: null,
        workflow: null,
        object_reference: null
      }
    },
    filters: {
      uid__in: objectId,
      object_type__tag__in: objectTypeTag,
      outgoing_relationships__to_object_id__in:
        outgoingRelationshipsToObjectIds,
      outgoing_relationships__forward_relation_name__in:
        outgoingRelationshipsForwardRelationName,
      workflow_mapping__wf_id__in: workflowMappingWorkflowId,
      order_by: orderBy,
      related_via: relatedVia
        ? {
            relation_name: relatedVia.relationName,
            related_object_uid: relatedVia.relatedObjectUid,
            object_type_tag: relatedVia.objectTypeTag
          }
        : undefined,
      name__icontains: nameIContains,
      status_tag__in: statusTagIn,
      status_tag__not_in: statusTagNotIn
    }
  };

  return useQuery<CreateDataObjectDetailsModelReturn>({
    enabled: config.enabled,
    retry: 0,
    queryKey: ["dataObjectsDetails", transformedParams, offset],
    queryFn: () => getDataObjectDetails(transformedParams, offset)
  });
};

type UseGetDataObjectWorkflowRelationActionsParams =
  | {
      objectTypeTag?: string;
      workflowKindTag: string;
      relation?: DataObjectWorkflowRelation;
    }
  | {
      workflowKindTag?: string;
      objectTypeTag: string;
      relation?: DataObjectWorkflowRelation;
    }
  | {
      objectTypeTag: string;
      workflowKindTag: string;
      relation?: DataObjectWorkflowRelation;
    };

export const useGetDataObjectWorkflowRelationActions = (
  params: UseGetDataObjectWorkflowRelationActionsParams,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectWorkflowRelationActions>
  > = {
    enabled: true
  }
) => {
  const { objectTypeTag, workflowKindTag, relation } = params;

  return useQuery({
    enabled: (!!objectTypeTag || !!workflowKindTag) && !!config?.enabled,
    queryKey: [
      "dataObjectWorkflowRelationActions",
      objectTypeTag,
      workflowKindTag,
      relation
    ],
    queryFn: () =>
      getDataObjectWorkflowRelationActions({
        objectTypeTag,
        workflowKindTag,
        relation
      })
  });
};

type UseGetDataObjectActivityLogsParams = {
  objectId: string;
};

export const useGetDataObjectActivityLogs = (
  params: UseGetDataObjectActivityLogsParams,
  config: UseQueryOptions<
    UseQueryReturnType<typeof getDataObjectActivityLogs>
  > = {
    enabled: true
  }
) => {
  const { objectId } = params;

  return useQuery({
    enabled: !!objectId,
    queryKey: ["dataObjectActivityLogs", objectId],
    queryFn: () => getDataObjectActivityLogs(objectId)
  });
};
